import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAccessToken } from '../auth/use-access-token.js';
import { removeCartLine, checkout } from '../api/use-cart.js';
import { useCartContext } from '../api/cart-provider.js';
import { when } from '../components/when.js';
import { useServices } from '../api/use-catalogue.js';
import { CartSubmit } from '../components/cart-cta.js';
import { ApiError } from '../api/client.js';
import { WaitNote } from '../components/wait-note.js';
import { Spinner } from '../components/spinner.js';

/**
 * Your cart — what is held, and for how long.
 *
 * A line is taken out on the line itself, by the × on its own row. There is no
 * button that empties the cart: somebody with three lessons in it almost always
 * wants to drop one, and a control that can only clear the lot is a trap.
 *
 * CHECKOUT IS ALL OR NOTHING. If an hour went while they were choosing, the API
 * refuses and names the lines it was about; nothing is booked, they take those
 * out, and the rest go through together. Half a cart is never booked.
 */
export function CartRoute() {
  const getToken = useAccessToken();
  const navigate = useNavigate();
  const { state, reload, releasedVisible } = useCartContext();
  const services = useServices();

  // WHICH LESSON, NOT JUST WHEN AND WHO. The row named the hour and the child
  // and left out the kind — and the kind is the one thing that could have been
  // picked wrong, because the catalogue offers several products against the
  // same hour. The cart is the last screen before money, so it is the last
  // chance to notice. The line already carries the tier; the catalogue already
  // knows its name, so nothing had to be asked of the API.
  const productNames = useMemo(() => {
    const byTier = new Map<string, string>();
    if (services.status === 'ready') {
      for (const service of services.value) {
        for (const product of service.products) byTier.set(product.tier_id, product.name);
      }
    }
    return byTier;
  }, [services]);

  // 'book', or 'remove:<line>' - so Book says "Booking…" and a line being taken out shows the spinner (RE-35).
  const [busy, setBusy] = useState<string | null>(null);
  const [gone, setGone] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const cart = state.status === 'ready' ? state.cart : null;

  async function remove(cartItemId: string) {
    if (busy) return;
    setBusy(`remove:${cartItemId}`);
    setError(null);
    try {
      await removeCartLine(getToken, cartItemId);
      setGone((current) => current.filter((id) => id !== cartItemId));
      reload();
    } catch {
      setError('That did not come out. Try again.');
    } finally {
      setBusy(null);
    }
  }

  async function book(event: FormEvent) {
    event.preventDefault();
    if (busy || !cart || cart.lines.length === 0) return;

    setBusy('book');
    setError(null);
    setGone([]);

    try {
      const booked = await checkout(getToken);
      // THE CART IS GONE, SO THE FRAME HAS TO BE TOLD. Found walking the deployed
      // build, 2 October: checkout deletes the cart, but the frame's copy of it
      // kept its lines and its clock, so the calendar still said "Go to cart 6:23"
      // over lessons that were already booked. Book pressed again on that copy
      // was refused, and the parent was told it had not gone through.
      reload();
      navigate('/cart/done', { state: { booked } });
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === 'slots_unavailable') {
        // The API says WHICH hours went. Marking those lines is the difference
        // between "something is wrong" and "this one, take it out".
        const slotIds = (cause as ApiError & { slot_ids?: string[] }).slot_ids ?? [];
        setGone(
          cart.lines
            .filter((line) => slotIds.includes(line.published_slot_id))
            .map((line) => line.cart_item_id),
        );
        setError('One of these filled up while you were choosing. Take it out and book the rest.');
      } else if (cause instanceof ApiError && cause.code === 'cart_expired') {
        setError('Your cart has expired. Your lessons were released — pick them again.');
        reload();
      } else if (cause instanceof ApiError && cause.code === 'no_live_cart') {
        // There is no cart on the server to book — it was booked, from here or
        // another tab, or it lapsed. "Try again" would be a lie: again finds the
        // same nothing. Re-reading shows the screen what is actually there.
        reload();
      } else {
        setError('That did not go through. Try again.');
      }
      setBusy(null);
    }
  }

  if (state.status === 'loading') return <WaitNote className="empty">Checking…</WaitNote>;
  if (state.status === 'failed') return <p className="empty">{state.message}</p>;

  if (!cart || cart.lines.length === 0) {
    // AN EMPTY CART SAYS NOTHING WHILE THE BANNER IS EXPLAINING WHY IT IS EMPTY.
    //
    // Ruled 30 September. A parent who took a phone call came back to "The hold
    // ran out" listing two hours with an Add on each - and then, directly below,
    // "Nothing in it yet." with a Pick a time button inviting her to start over.
    // Both true, and they argue: the weaker action sat at the bottom where the
    // eye lands, four inches under the button that would give her those exact
    // hours back.
    //
    // The head stays, so the screen still says where she is. Once the banner has
    // gone, the empty cart reads exactly as it always did.
    return (
      <>
        <div className="page-head">
          <Link className="back" to="/booking" aria-label="Back to the calendar">
            ‹
          </Link>
          <h2>Your cart</h2>
        </div>
        {releasedVisible ? null : (
          <>
            <p className="empty">Nothing in it yet.</p>
            <Link className="primary" to="/booking">
              Book a lesson
            </Link>
          </>
        )}
      </>
    );
  }

  return (
    <>
      <div className="page-head">
        <Link className="back" to="/booking" aria-label="Back to the calendar">
          ‹
        </Link>
        <h2>Your cart</h2>
      </div>

      <form onSubmit={book}>
        {cart.lines.map((line) => (
          <div key={line.cart_item_id} className={gone.includes(line.cart_item_id) ? 'cart-line gone' : 'cart-line'}>
            <span className="cart-line-text">
              <span className="name">{when(line.slot_start)}</span>
              <span className="detail">
                {[line.clinic_title ?? productNames.get(line.capacity_tier_id), line.athlete_name]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
              {gone.includes(line.cart_item_id) ? (
                <span className="warn-note">This hour filled up while you were choosing.</span>
              ) : null}
            </span>
            <button
              type="button"
              className="drop"
              aria-label={`Take ${line.athlete_name} out of ${when(line.slot_start)}`}
              onClick={() => remove(line.cart_item_id)}
              disabled={busy !== null}
              aria-busy={busy === `remove:${line.cart_item_id}`}
            >
              {busy === `remove:${line.cart_item_id}` ? <Spinner /> : '×'}
            </button>
          </div>
        ))}

        {error ? <p className="state-note">{error}</p> : null}

        <CartSubmit label="Book" expiresAt={cart.expires_at} disabled={busy !== null} busy={busy === 'book'} working="Booking…" onExpired={reload} />
      </form>
    </>
  );
}
